'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import {
  LOGO_SRC_DARK_BACKGROUND,
  LOGO_SRC_LIGHT_BACKGROUND,
  logoWordmarkWidthForHeight,
} from '@/components/logo';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon } from '@/components/ui/icon';
import { NewTwitterIcon } from '@/components/ui/icon/brands';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { cn } from '@/lib/ui/utils';

export type GiftHandleBadgeSize = 'sm' | 'md' | 'lg';

export interface GiftHandleBadgeProps {
  /** Identity platform that owns the handle. */
  platform: 'morfi' | 'x';
  /** Raw handle, no leading "@". */
  handle: string;
  /** When true, render the localized "Anonymous Morfian" label and no platform icon. */
  anonymous?: boolean;
  /** Display size to match surrounding type. Default `md`. */
  size?: GiftHandleBadgeSize;
  /** Optional className passthrough — merged via `cn()` onto the wrapper span. */
  className?: string;
}

interface SizeTokens {
  /** Tailwind size-* class for the icon glyph (square). */
  icon: string;
  /** Pixel dimension passed to next/image for the Morfi mark. */
  imagePx: number;
  /** Tailwind text-* class for the handle label. */
  text: string;
  /** Tailwind gap-* class between icon and label. */
  gap: string;
}

const SIZE_TOKENS: Record<GiftHandleBadgeSize, SizeTokens> = {
  sm: { icon: 'size-3', imagePx: 12, text: 'text-caption', gap: 'gap-1' },
  md: { icon: 'size-3.5', imagePx: 14, text: 'text-body', gap: 'gap-1' },
  lg: { icon: 'size-4', imagePx: 16, text: 'text-body-lg', gap: 'gap-1.5' },
};

/**
 * Inline identity badge that disambiguates Morfi handles from X handles.
 *
 * Prevents the `@cam_tutor` (Morfi) vs `@morfimarkets` (X) collision by
 * showing the platform glyph immediately before the handle. Anonymous
 * deposits render the localized "Anonymous Morfian" label with no glyph.
 *
 * Render shape: `[icon] @handle`. The handle text node remains a single
 * string ("@handle") so callsite test assertions like
 * `screen.getByText('@alice')` keep matching.
 */
export function GiftHandleBadge(props: GiftHandleBadgeProps): React.ReactNode {
  const t = useTranslations('gift.handle_badge');
  const tContrib = useTranslations('gift.contributors');
  const { resolvedTheme } = useTheme();
  const mounted = useHydrated();
  const size: GiftHandleBadgeSize = props.size ?? 'md';
  const tokens = SIZE_TOKENS[size];
  const isAnonymous = props.anonymous === true;

  const isDark = mounted && resolvedTheme === 'dark';
  const morfiLogoSrc = isDark ? LOGO_SRC_DARK_BACKGROUND : LOGO_SRC_LIGHT_BACKGROUND;
  const morfiLogoHeight = tokens.imagePx;
  const morfiLogoWidth = logoWordmarkWidthForHeight(morfiLogoHeight);

  if (isAnonymous) {
    return (
      <span
        className={cn(
          'inline-flex items-center font-medium italic text-foreground-secondary',
          tokens.text,
          props.className,
        )}
        data-testid="gift-handle-badge-anonymous"
      >
        {tContrib('anonymous_sender')}
      </span>
    );
  }

  const handleText = `@${props.handle}`;
  const isX = props.platform === 'x';
  const tooltip = isX ? t('x_tooltip') : t('morfi_tooltip');
  const ariaLabel = isX
    ? t('x_aria', { handle: props.handle })
    : t('morfi_aria', { handle: props.handle });

  return (
    <span
      className={cn('inline-flex items-center', tokens.gap, props.className)}
      data-testid="gift-handle-badge"
      data-platform={props.platform}
    >
      {/* SR-only label disambiguates Morfi vs X for assistive tech. */}
      <span className="sr-only">{ariaLabel}</span>
      <AppTooltip content={tooltip} side="top">
        <span className={cn('inline-flex shrink-0 items-center', tokens.icon)} aria-hidden="true">
          {isX ? (
            <Icon icon={NewTwitterIcon} className={tokens.icon} />
          ) : (
            <Image
              src={morfiLogoSrc}
              alt=""
              width={morfiLogoWidth}
              height={morfiLogoHeight}
              className="h-auto max-h-full w-auto"
            />
          )}
        </span>
      </AppTooltip>
      <span className={cn('font-medium', tokens.text)} aria-hidden="true">
        {handleText}
      </span>
    </span>
  );
}
